import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { BanknoteIcon, CalendarIcon, LockIcon, UserIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, XIcon, LoaderIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
type ProcessingStatus = 'idle' | 'processing' | 'success' | 'error';
export function VirtualTerminal() {
  const {
    t
  } = useTranslation('payments');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('idle');
  const [reference, setReference] = useState('');
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors placeholder:text-gray-400';
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').substring(0, 19);
  };
  const formatExpiryDate = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 2) return `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
    return digits;
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(formatExpiryDate(e.target.value));
  };
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCvv(e.target.value.replace(/\D/g, '').substring(0, 4));
  };
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    if (value.split('.').length > 2) return;
    setAmount(value);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardNumber || !expiryDate || !cvv || !cardholderName || !amount) return;
    setProcessingStatus('processing');
    const newReference = `REF-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
    setReference(newReference);
    setTimeout(() => {
      setProcessingStatus(Math.random() < 0.8 ? 'success' : 'error');
    }, 2000);
  };
  const resetForm = () => {
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setCardholderName('');
    setAmount('');
    setDescription('');
    setProcessingStatus('idle');
    setReference('');
  };
  const closeModal = () => {
    if (processingStatus === 'success') resetForm();else setProcessingStatus('idle');
  };
  const isFormValid = cardNumber && expiryDate && cvv && cardholderName && amount;
  return <div className="max-w-2xl mx-auto" data-id="element-8169">
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-8170">
        {/* ─── PAYMENT DETAILS ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-8171">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8172">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8173">
              <BanknoteIcon size={16} className="mr-2 text-[#B38A00]" data-id="element-8174" />
              {t('components.FixedLinksPayment.payment_details')}
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-8175">
              {t('pages.VirtualTerminal.process_a_mail_order_telephone_order')}
            </p>
          </div>

          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8176">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8177">
              {t('components.BulkPaymentPreview.amount')}{' '}
              <span className="text-red-400" data-id="element-8178">*</span>
            </label>
            <div className="relative" data-id="element-8179">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8180">
                <span className="text-sm font-medium text-gray-500" data-id="element-8181">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={handleAmountChange} data-id="element-8182" />
            </div>
          </div>

          <div className="px-6 py-4" data-id="element-8183">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8184">
              {t('components.BulkPaymentPreview.description')}{' '}
              <span className="normal-case text-gray-400" data-id="element-8185">
                {t('components.RecurringPayment.optional')}
              </span>
            </label>
            <input type="text" className={inputClass} placeholder={t('pages.VirtualTerminal.attrs.eg_product_purchase')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-8186" />
          </div>
        </div>

        {/* ─── CARD INFORMATION ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-8187">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8188">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8189">
              <CreditCardIcon size={16} className="mr-2 text-[#B38A00]" data-id="element-8190" />
              {t('pages.VirtualTerminal.card_information')}
            </h2>
          </div>

          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8191">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8192">
              {t('pages.VirtualTerminal.card_number')}{' '}
              <span className="text-red-400" data-id="element-8193">*</span>
            </label>
            <div className="relative" data-id="element-8194">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8195">
                <CreditCardIcon size={14} className="text-gray-400" data-id="element-8196" />
              </div>
              <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.xxxx_xxxx_xxxx_xxxx')} value={cardNumber} onChange={handleCardNumberChange} data-id="element-8197" />
            </div>
          </div>

          <div className="px-6 py-4 border-b border-gray-200 grid grid-cols-2 gap-4" data-id="element-8198">
            <div data-id="element-8199">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8200">
                {t('components.FixedLinksPayment.expiry_date')}{' '}
                <span className="text-red-400" data-id="element-8201">*</span>
              </label>
              <div className="relative" data-id="element-8202">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8203">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-8204" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.mmyy')} value={expiryDate} onChange={handleExpiryDateChange} data-id="element-8205" />
              </div>
            </div>
            <div data-id="element-8206">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8207">
                {t('pages.VirtualTerminal.cvv_cvc')}{' '}
                <span className="text-red-400" data-id="element-8208">*</span>
              </label>
              <div className="relative" data-id="element-8209">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8210">
                  <LockIcon size={14} className="text-gray-400" data-id="element-8211" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.xxx')} value={cvv} onChange={handleCvvChange} data-id="element-8212" />
              </div>
            </div>
          </div>

          <div className="px-6 py-4" data-id="element-8213">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8214">
              {t('pages.VirtualTerminal.cardholder_name')}{' '}
              <span className="text-red-400" data-id="element-8215">*</span>
            </label>
            <div className="relative" data-id="element-8216">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8217">
                <UserIcon size={14} className="text-gray-400" data-id="element-8218" />
              </div>
              <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.name_as_it_appears_on_card')} value={cardholderName} onChange={e => setCardholderName(e.target.value)} data-id="element-8219" />
            </div>
          </div>
        </div>

        {/* ─── SECURITY NOTE ─── */}
        <div className="flex items-center gap-2 px-1 text-xs text-gray-400" data-id="element-8220">
          <LockIcon size={12} className="flex-shrink-0" data-id="element-8221" />
          <span data-id="element-8222">
            {t('pages.VirtualTerminal.card_information_is_securely_processed_and')}
          </span>
        </div>

        {/* ─── SUBMIT ─── */}
        <div className="flex justify-end" data-id="element-8223">
          <button type="submit" disabled={!isFormValid} className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm ${isFormValid ? 'bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-8224">
            {t('pages.VirtualTerminal.process_payment')}
          </button>
        </div>
      </form>

      {/* ─── RESULT MODAL ─── */}
      {processingStatus !== 'idle' && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} data-id="element-8225">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-8226">
              {/* Processing */}
              {processingStatus === 'processing' && <div className="p-8 flex flex-col items-center" data-id="element-8227">
                  <div className="w-14 h-14 rounded-full bg-[#FFC70A]/10 flex items-center justify-center mb-4" data-id="element-8228">
                    <LoaderIcon size={24} className="text-[#B38A00] animate-spin" data-id="element-8229" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-8230">
                    {t('pages.VirtualTerminal.processing_payment')}
                  </h3>
                  <p className="text-sm text-gray-500 text-center" data-id="element-8231">
                    {t('pages.VirtualTerminal.please_wait_while_we_process_the')}
                  </p>
                </div>}

              {/* Success */}
              {processingStatus === 'success' && <>
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-8232">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-8233">
                      {t('pages.VirtualTerminal.payment_successful')}
                    </h3>
                    <button onClick={closeModal} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-8234">
                      <XIcon size={18} aria-hidden="true" data-id="element-8235" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col items-center" data-id="element-8236">
                    <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-4" data-id="element-8237">
                      <CheckCircleIcon size={28} className="text-green-600" data-id="element-8238" />
                    </div>
                    <p className="text-sm text-gray-500 text-center mb-5" data-id="element-8239">
                      {t('pages.VirtualTerminal.the_transaction_has_been_approved')}
                    </p>

                    {/* Transaction details */}
                    <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden mb-5" data-id="element-8240">
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-8241">
                        <span className="text-xs text-gray-500" data-id="element-8242">
                          {t('components.BulkPaymentPreview.amount')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-8243">
                          {formatCurrencyAmount(parseFloat(amount || '0'), {
                    fractionDigits: 2
                  })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-8244">
                        <span className="text-xs text-gray-500" data-id="element-8245">
                          {t('components.BulkPaymentPreview.reference')}
                        </span>
                        <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-8246">
                          {reference}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-8247">
                        <span className="text-xs text-gray-500" data-id="element-8248">
                          {t('components.CardOnFilePayment.card')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-8249">
                          •••• {cardNumber.slice(-4)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5" data-id="element-8250">
                        <span className="text-xs text-gray-500" data-id="element-8251">
                          {t('pages.VirtualTerminal.date')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-8252">
                          {new Date().toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <button onClick={closeModal} className="px-5 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-8253">
                      {t('pages.VirtualTerminal.new_transaction')}
                    </button>
                  </div>
                </>}

              {/* Error */}
              {processingStatus === 'error' && <>
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-8254">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-8255">
                      {t('pages.VirtualTerminal.transaction_failed')}
                    </h3>
                    <button onClick={closeModal} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-8256">
                      <XIcon size={18} aria-hidden="true" data-id="element-8257" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col items-center" data-id="element-8258">
                    <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4" data-id="element-8259">
                      <XCircleIcon size={28} className="text-red-600" data-id="element-8260" />
                    </div>
                    <p className="text-sm text-gray-500 text-center mb-4" data-id="element-8261">
                      {t('pages.VirtualTerminal.the_payment_could_not_be_processed')}
                    </p>

                    <div className="w-full bg-red-50 border border-red-100 rounded-lg p-3 mb-5" data-id="element-8262">
                      <p className="text-xs text-red-700" data-id="element-8263">
                        {t('pages.VirtualTerminal.transaction_declined_by_issuing_bank_please')}
                      </p>
                    </div>

                    <button onClick={closeModal} className="px-5 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-8264">
                      {t('pages.VirtualTerminal.try_again')}
                    </button>
                  </div>
                </>}
            </div>
          </div>, document.body)}
    </div>;
}